// Steer, then queue (CONTRACT v1): messages a person sends while Poneva works.
//
// One store per page, keyed by thread. The transport, the composer, the
// waiting bubbles and the end-of-answer drain all read and write it, so a
// reload or a chat switch in this tab finds the same waiting messages
// (sessionStorage, like the resumable stream id). Read messages leave the store:
// from then on they live in the answer as `data-steer` parts.

import type { CompleteAttachment } from '@assistant-ui/react';

export type SteerHold = 'stopped' | 'question' | 'approval' | 'credits' | 'failed';

/**
 * - `posting`: the POST to the answer's inbox is in flight.
 * - `waiting`: in the inbox; Poneva reads it at its next step.
 * - `withdrawing`: Edit/Remove is not confirmed; never automatically send it again.
 * - `next`: no step left to read it at; it goes as the next message.
 * - `held`: it waits for the person (see `hold`).
 */
export type SteerState = 'posting' | 'waiting' | 'withdrawing' | 'next' | 'held';

export type SteerEntry = {
  id: string;
  text: string;
  /** Completed upload references; attachments never enter the running inbox. */
  attachments?: CompleteAttachment[];
  state: SteerState;
  hold?: SteerHold;
  /** The answer it was posted to, while it can still be read there. */
  streamId?: string;
};

type Snapshot = readonly SteerEntry[];

const KEY = (threadId: string) => `poneva.chat.steer.${threadId}`;
const EMPTY: Snapshot = Object.freeze([]);

function readStored(threadId: string): SteerEntry[] {
  try {
    const raw = globalThis.sessionStorage?.getItem(KEY(threadId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { entries?: unknown; consumed?: unknown };
    const entries = Array.isArray(parsed.entries) ? parsed.entries : [];
    return entries.flatMap((value): SteerEntry[] => {
      if (!value || typeof value !== 'object') return [];
      const entry = value as Partial<SteerEntry>;
      if (typeof entry.id !== 'string' || typeof entry.text !== 'string') return [];
      // A reload ends this tab's part in the answer: what was not read yet waits
      // for the person (never sent by itself), until the resumed answer reads it.
      return [
        {
          id: entry.id,
          text: entry.text,
          ...(Array.isArray(entry.attachments) ? { attachments: entry.attachments } : {}),
          state: 'held' as const,
          hold: entry.state === 'held' && entry.hold ? entry.hold : ('stopped' as const),
          ...(entry.streamId ? { streamId: entry.streamId } : {}),
        },
      ];
    });
  } catch {
    return [];
  }
}

function readConsumed(threadId: string): string[] {
  try {
    const raw = globalThis.sessionStorage?.getItem(KEY(threadId));
    const parsed = raw ? (JSON.parse(raw) as { consumed?: unknown }) : null;
    return Array.isArray(parsed?.consumed)
      ? parsed.consumed.filter((id): id is string => typeof id === 'string').slice(-200)
      : [];
  } catch {
    return [];
  }
}

export function createSteerStore() {
  const entries = new Map<string, SteerEntry[]>();
  // Ids already sent, read or cleared: a late `data-steerUnread` for one of them
  // must never bring it back.
  const consumed = new Map<string, Set<string>>();
  const snapshots = new Map<string, Snapshot>();
  const listeners = new Set<() => void>();

  function list(threadId: string): SteerEntry[] {
    let current = entries.get(threadId);
    if (!current) {
      current = readStored(threadId);
      entries.set(threadId, current);
      consumed.set(threadId, new Set(readConsumed(threadId)));
    }
    return current;
  }

  function done(threadId: string): Set<string> {
    list(threadId);
    return consumed.get(threadId)!;
  }

  function commit(threadId: string, next: SteerEntry[]) {
    entries.set(threadId, next);
    snapshots.delete(threadId);
    try {
      const used = [...done(threadId)].slice(-200);
      if (next.length || used.length)
        globalThis.sessionStorage?.setItem(
          KEY(threadId),
          JSON.stringify({ entries: next, consumed: used }),
        );
      else globalThis.sessionStorage?.removeItem(KEY(threadId));
    } catch {
      /* Private mode: the waiting messages still work for this page. */
    }
    for (const listener of [...listeners]) listener();
  }

  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    snapshot(threadId: string | null | undefined): Snapshot {
      if (!threadId) return EMPTY;
      const cached = snapshots.get(threadId);
      if (cached) return cached;
      const next = Object.freeze([...list(threadId)]);
      snapshots.set(threadId, next);
      return next;
    },
    add(threadId: string, entry: SteerEntry) {
      commit(threadId, [...list(threadId).filter((item) => item.id !== entry.id), entry]);
    },
    update(threadId: string, id: string, patch: Partial<Omit<SteerEntry, 'id'>>) {
      const current = list(threadId);
      if (!current.some((item) => item.id === id)) return;
      commit(
        threadId,
        current.map((item) => {
          if (item.id !== id) return item;
          const next = { ...item, ...patch };
          if (next.state !== 'held') delete next.hold;
          return next;
        }),
      );
    },
    /** Gone for good: sent, read, removed or cleared. */
    consume(threadId: string, ids: readonly string[]) {
      if (!ids.length) return;
      const used = done(threadId);
      const current = list(threadId);
      const fresh = ids.filter((id) => !used.has(id));
      if (!fresh.length && !current.some((item) => ids.includes(item.id))) return;
      for (const id of ids) used.add(id);
      commit(
        threadId,
        current.filter((item) => !ids.includes(item.id)),
      );
    },
    /** Every message waiting for the person keeps waiting, now for this reason. */
    relabelHolds(threadId: string, hold: SteerHold) {
      const current = list(threadId);
      if (!current.some((item) => item.state === 'held' && item.hold !== hold)) return;
      commit(
        threadId,
        current.map((item) => (item.state === 'held' ? { ...item, hold } : item)),
      );
    },
    isConsumed(threadId: string, id: string) {
      return done(threadId).has(id);
    },
    /** Everything not yet read waits for the person, for this reason. */
    holdAll(threadId: string, hold: SteerHold) {
      const current = list(threadId);
      if (!current.length) return;
      commit(
        threadId,
        current.map((item) => ({ ...item, state: 'held', hold })),
      );
    },
    /** The answer ended: whatever it did not read goes next (or keeps its hold). */
    settle(threadId: string) {
      const current = list(threadId);
      if (!current.some((item) => ['posting', 'waiting', 'withdrawing'].includes(item.state)))
        return;
      commit(
        threadId,
        current.map((item) =>
          item.state === 'withdrawing'
            ? { ...item, state: 'held' as const, hold: 'stopped' as const }
            : item.state === 'posting' || item.state === 'waiting'
              ? { id: item.id, text: item.text, state: 'next' as const }
              : item,
        ),
      );
    },
    /**
     * The answer reported messages it never read (`data-steerUnread`): known ones
     * go next or wait; unknown ones (a reload, another device) come back held, so
     * nothing is lost and nothing is sent unattended.
     */
    unread(
      threadId: string,
      reason: 'finished' | 'stopped' | 'error',
      messages: readonly { id: string; text: string }[],
    ) {
      const current = list(threadId);
      const used = done(threadId);
      const byId = new Map(current.map((item) => [item.id, item]));
      const hold: SteerHold | null =
        reason === 'stopped' ? 'stopped' : reason === 'error' ? 'failed' : null;
      let changed = false;
      const next = current.map((item) => {
        if (
          !messages.some((message) => message.id === item.id) ||
          item.state === 'held' ||
          item.state === 'next'
        )
          return item;
        changed = true;
        if (item.state === 'withdrawing')
          return { ...item, state: 'held' as const, hold: hold ?? ('stopped' as const) };
        return hold
          ? { id: item.id, text: item.text, state: 'held' as const, hold }
          : { id: item.id, text: item.text, state: 'next' as const };
      });
      for (const message of messages) {
        if (byId.has(message.id) || used.has(message.id)) continue;
        changed = true;
        next.push({ id: message.id, text: message.text, state: 'held', hold: hold ?? 'stopped' });
      }
      if (changed) commit(threadId, next);
    },
    /** Test seam. */
    reset() {
      entries.clear();
      consumed.clear();
      snapshots.clear();
      for (const listener of [...listeners]) listener();
    },
  };
}

export const steerStore = createSteerStore();

export function newSteerId(): string {
  const random = globalThis.crypto?.randomUUID?.().replaceAll('-', '');
  return `st_${random ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`}`;
}
